type BaseCondition = { type: 'country'; countryCodes: string[] }

type Condition =
  | BaseCondition
  | { type: 'all'; conditions: Condition[] }
  | { type: 'any'; conditions: Condition[] }

interface Variant<T> {
  condition?: Condition
  value: T
}

// Feature flag configuration with conditional variants and default fallback
interface VariantConfig<T> {
  variants: Variant<T>[]
  value: T // default/fallback value
}

// Context used to evaluate conditions at runtime
interface EvaluationContext {
  countryCode?: string
}

/**
 * Type guard to check if a value is a VariantConfig
 * (used to distinguish from simple static configs)
 */
export function isVariantConfig<T>(value: unknown): value is VariantConfig<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'variants' in value &&
    Array.isArray((value as VariantConfig<T>).variants) &&
    'value' in value
  )
}

/**
 * Evaluates a condition against the provided context.
 * Returns true if the condition matches, false otherwise.
 */
function evaluateCondition(
  condition: Condition,
  ctx: EvaluationContext,
): boolean {
  switch (condition.type) {
    case 'all': {
      return condition.conditions.every((c) => evaluateCondition(c, ctx))
    }
    case 'any': {
      return condition.conditions.some((c) => evaluateCondition(c, ctx))
    }
    case 'country': {
      return ctx.countryCode
        ? condition.countryCodes.includes(ctx.countryCode)
        : false
    }
    default: {
      console.error(
        'Unknown condition type:',
        (condition as { type: string }).type,
      )
      // If nothing matches, the condition is not valid, so we return false and use default value instead
      return false
    }
  }
}

/**
 * Resolves the appropriate value from a VariantConfig based on runtime context.
 * Evaluates variants in order and returns the first matching variant's value.
 * Falls back to default value if no variants match.
 */
export function resolveValue<T>(
  config: VariantConfig<T>,
  ctx: EvaluationContext,
): T {
  for (const variant of config.variants) {
    if (!variant.condition || evaluateCondition(variant.condition, ctx)) {
      return variant.value
    }
  }

  return config.value
}
